// Settings → App Downloads (Neil only). One card per iOS app, each with its own
// switch for that app's public download page (/apps/harvi, /apps/lodge).
//
// OFF is the default and the safe state: the public page then says the app
// isn't available yet and never receives the unlisted App Store link. Switch a
// page on only once Apple has released that app — the card says so, and links
// the store page so it can be checked first.
import { useState } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { Btn, Spinner, ErrorMsg, Card, Badge, useToast, SectionHeader } from '../ui';
import { formatInstant } from '../../utils/format';

const COPY = {
  harvi: 'For the Harvus team. Turn on once Apple has released Harvi.',
  lodge: 'For outfitter guides and lodge staff. Turn on once Apple has released Harvus Lodge.',
};

function AppCard({ app, onChanged }) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const pageUrl = `${window.location.origin}${app.pagePath}`;

  const flip = async () => {
    const next = !app.enabled;
    if (next && !window.confirm(`Turn on the ${app.name} download page?\n\nAnyone with ${pageUrl} will see the App Store link. Make sure Apple has released ${app.name} first — open the store link on an iPhone to check.`)) return;
    setSaving(true);
    try {
      const updated = await api.put(`app-downloads/${app.app}`, { enabled: next });
      onChanged(updated);
      toast(`${app.name} download page is now ${updated.enabled ? 'on' : 'off'}.`);
    } catch (e) {
      toast(`Could not change the ${app.name} page: ${e.message}`, 'error');
    } finally { setSaving(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(pageUrl); toast('Page link copied.'); }
    catch { toast(pageUrl); }
  };

  return (
    <Card style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/app-icon-harvus.png" alt="" width={40} height={40} style={{ borderRadius: 10, border: `1px solid ${B.border}` }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 16, color: B.text }}>{app.name}</div>
            <div style={{ fontSize: 13, color: B.textMd }}>{COPY[app.app] || app.audience}</div>
          </div>
        </div>
        <Badge color={app.enabled ? B.green : B.textLt}>{app.enabled ? 'On — downloads offered' : 'Off — not offered'}</Badge>
      </div>

      <div style={{ fontSize: 14, color: B.textMd, lineHeight: 1.6 }}>
        <div>Public page: <a href={pageUrl} target="_blank" rel="noreferrer" style={{ color: B.navy }}>{pageUrl}</a></div>
        <div>App Store: <a href={app.storeUrl} target="_blank" rel="noreferrer" style={{ color: B.navy }}>{app.storeUrl}</a></div>
        <div style={{ color: B.textLt, fontSize: 13 }}>
          {app.updatedUTC ? `Last changed ${formatInstant(app.updatedUTC)}${app.updatedBy ? ` by ${app.updatedBy}` : ''}` : 'Never turned on'}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Btn onClick={flip} loading={saving} variant={app.enabled ? 'secondary' : 'primary'}>
          {app.enabled ? 'Turn off' : 'Turn on'}
        </Btn>
        <Btn onClick={copy} variant="ghost">Copy page link</Btn>
      </div>
    </Card>
  );
}

export function AppDownloadsPanel() {
  const { data, loading, error, reload } = useApi(() => api.get('app-downloads'), []);
  const [overrides, setOverrides] = useState({});

  if (loading) return <Spinner />;
  if (error) return <ErrorMsg msg={error} onRetry={reload} />;
  const apps = (data?.apps || []).map((a) => overrides[a.app] || a);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SectionHeader title="App download pages" />
      <p style={{ margin: 0, color: B.textMd, fontSize: 14, maxWidth: 720 }}>
        Each app has its own public download page. While a page is off, it says the app isn't available yet and
        the App Store link is not sent to anyone's browser. The combined page at /apps follows the same switches.
        A change reaches the public pages within about a minute.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
        {apps.map((a) => (
          <AppCard key={a.app} app={a} onChanged={(u) => setOverrides((o) => ({ ...o, [u.app]: u }))} />
        ))}
      </div>
    </div>
  );
}
